/* ============================================================
   The narrow-window chrome, shown under Tailwind's `md` in place of the
   sidebar: a frosted top bar (lockup, search, menu), a bottom tab bar with
   the four pages (the phone app's shape, so a hand that knows one knows the
   other), and a menu sheet holding what the sidebar holds below its nav —
   projects, tags, sync, the account — plus the one thing a phone browser
   should always offer: the link to the app itself.
   ============================================================ */
import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { todayStr, useAuth, useStore, useSync } from '@habishine/client-core';

import { Icon } from '../components/Icon';
import { Ring } from '../components/Ring';
import { SunMark } from '../components/SunMark';
import { Avatar } from '../components/ui';
import { APP_STORE_URL } from '../config';
import { hueVar } from '../theme/hues';
import { useEditors } from './Editors';
import { usePhone } from './useNarrow';
import { PAGES, PHASE } from './Sidebar';

const openPalette = () => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }));

export function TopBar({ onMenu }: { onMenu: () => void }) {
  const { habitsForDay, tasksForDay, doneForDay } = useStore();
  // a real phone (narrow AND a finger) gets a standing "Get app" chip in the
  // bar — Notion / Todoist's pattern; a desktop window dragged narrow has no
  // app to install and sees nothing
  const phone = usePhone();
  const today = todayStr();
  const due = habitsForDay(today).filter((h) => h.dueToday);
  const total = due.length + tasksForDay(today).length + doneForDay(today).length;
  const done = due.filter((h) => h.done).length + doneForDay(today).length;
  return (
    <header className="glass sticky top-0 z-[5] flex items-center gap-2 border-x-0 border-t-0 px-3 pb-2 pt-[calc(8px+env(safe-area-inset-top))] md:hidden">
      <NavLink to="/today" className="flex items-center gap-2.5 pl-1">
        <span className="relative flex h-8 w-8 items-center justify-center">
          <span className="absolute inset-0"><Ring size={32} stroke={3} hue="yellow" pct={total ? done / total : 0} track="sunk" /></span>
          <SunMark size={19} />
        </span>
        <span className="wordmark text-[20px] text-ink">habishine</span>
      </NavLink>
      <span className="flex-1" />
      {phone ? (
        <a href={APP_STORE_URL} target="_blank" rel="noopener" className="sun-gradient mr-0.5 rounded-full px-3 py-1.5 text-[12px] font-bold text-white shadow-[0_6px_14px_-6px_rgba(245,133,28,0.7)] active:scale-[0.97]">
          Get app
        </a>
      ) : null}
      <button type="button" aria-label="Search" onClick={openPalette} className="flex h-10 w-10 items-center justify-center rounded-full text-ink-2 active:bg-card-sunk">
        <Icon name="search" size={19} strokeWidth={2.3} />
      </button>
      <button type="button" aria-label="Menu" onClick={onMenu} className="flex h-10 w-10 items-center justify-center rounded-full text-ink-2 active:bg-card-sunk">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden><path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
      </button>
    </header>
  );
}

export function TabBar() {
  return (
    <nav className="glass fixed inset-x-0 bottom-0 z-[5] border-x-0 border-b-0 pb-[env(safe-area-inset-bottom)] md:hidden" aria-label="Pages">
      <div className="grid grid-cols-4 px-2 pt-1.5 pb-1.5">
        {PAGES.map((p) => (
          <NavLink key={p.to} to={p.to} className="flex flex-col items-center gap-1 rounded-[14px] py-1.5 text-[10.5px] font-semibold">
            {({ isActive }) => (
              <>
                <span
                  className="flex h-8 w-12 items-center justify-center rounded-full transition-colors duration-150"
                  style={isActive ? { background: hueVar(p.hue, 't'), color: hueVar(p.hue, 'k') } : { color: 'var(--tertiary)' }}
                >
                  <Icon name={p.icon} size={19} strokeWidth={2.3} />
                </span>
                <span className={isActive ? 'text-ink' : 'text-tertiary'}>{p.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

export function MenuSheet({ onClose }: { onClose: () => void }) {
  const { activeProjects, activeTags, projectOpenCount, projectProgress, tagOpenCount } = useStore();
  const sync = useSync();
  const { user } = useAuth();
  const { editProject } = useEditors();
  const navigate = useNavigate();
  const location = useLocation();
  const phase = PHASE[sync.phase];
  const go = (to: string) => { onClose(); navigate(to); };
  // route changes close it (the back button, a tab tap under the scrim)
  const [path] = useState(location.pathname);
  useEffect(() => { if (location.pathname !== path) onClose(); }, [location.pathname, path, onClose]);

  const projects = activeProjects();
  const tags = activeTags();
  const row = 'flex w-full items-center gap-3 rounded-[14px] px-3 py-3 text-left text-[15px] text-ink active:bg-card-sunk';

  return (
    <div className="fade-in fixed inset-0 z-30 flex items-end bg-[rgba(27,27,31,0.32)] backdrop-blur-[6px] md:hidden" onMouseDown={onClose}>
      <div role="dialog" aria-label="Menu" className="surface-pop sheet-up max-h-[86vh] w-full overflow-y-auto rounded-b-none rounded-t-[26px] p-3 pb-[calc(12px+env(safe-area-inset-bottom))]" onMouseDown={(e) => e.stopPropagation()}>
        <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-hairline" aria-hidden />

        <a href={APP_STORE_URL} target="_blank" rel="noopener" className="mb-2 flex items-center gap-3 rounded-row p-3" style={{ background: 'linear-gradient(135deg, var(--sky-dawn-top), var(--sky-dawn-horizon))' }}>
          <SunMark size={40} />
          <span className="min-w-0 flex-1">
            <span className="block text-[15px] font-bold text-ink">Get the Habishine app</span>
            <span className="block text-[12.5px] leading-snug text-ink-2">Reminders, the Lock Screen card and the widget live on your phone.</span>
          </span>
          <span className="text-meta"><Icon name="chevron-right" size={18} /></span>
        </a>

        <SheetSection title="Projects" action={<button type="button" aria-label="New project" onClick={() => { onClose(); editProject(null); }} className="rounded-full p-1.5 text-tertiary active:bg-card-sunk"><Icon name="plus" size={16} strokeWidth={2.4} /></button>}>
          {projects.length === 0 ? <p className="px-3 py-2 text-[13px] text-tertiary">Tap + to make one, or type #Name in capture.</p> : null}
          {projects.map((p) => {
            const prog = projectProgress(p.id);
            return (
              <button key={p.id} type="button" className={row} onClick={() => go(`/project/${p.id}`)}>
                <span className="flex w-6 items-center justify-center">
                  {prog.total > 0 ? <Ring size={18} stroke={3} hue={p.hue} pct={prog.done / prog.total} check={prog.done === prog.total} /> : <span className="h-3 w-3 rounded-full" style={{ background: hueVar(p.hue, 'c') }} />}
                </span>
                <span className="flex-1 truncate">{p.name}</span>
                <Count n={projectOpenCount(p.id)} />
              </button>
            );
          })}
        </SheetSection>

        <SheetSection title="Tags">
          {tags.length === 0 ? <p className="px-3 py-2 text-[13px] text-tertiary">Type @name in capture to make one.</p> : null}
          {tags.map((t) => (
            <button key={t.id} type="button" className={row} onClick={() => go(`/tag/${t.id}`)}>
              <span className="flex w-6 items-center justify-center" style={{ color: hueVar(t.hue, 'c') }}><Icon name="tag" size={17} strokeWidth={2.3} /></span>
              <span className="flex-1 truncate">{t.name}</span>
              <Count n={tagOpenCount(t.id)} />
            </button>
          ))}
        </SheetSection>

        <div className="mt-3 border-t border-hairline pt-2">
          <button type="button" onClick={sync.syncNow} className="flex w-full items-center gap-3 rounded-[14px] px-3 py-2.5 text-left text-[13.5px] text-meta active:bg-card-sunk">
            <span className={`flex w-6 justify-center ${phase.cls}`}><Icon name={phase.icon} size={15} strokeWidth={2.4} /></span>
            <span className="flex-1 truncate">{phase.label}</span>
            {sync.pending > 0 ? <span className="rounded-full bg-card-sunk px-2 text-[11px] font-bold text-ink-2">{sync.pending}</span> : null}
          </button>
          <button type="button" className={row} onClick={() => go('/settings')}>
            <span className="flex w-6 justify-center"><Avatar email={user?.email} size={24} /></span>
            <span className="min-w-0 flex-1 truncate text-[14px]">{user?.email ?? 'Settings'}</span>
            <span className="text-tertiary"><Icon name="settings" size={16} strokeWidth={2.2} /></span>
          </button>
        </div>
      </div>
    </div>
  );
}

function SheetSection({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mt-2">
      <div className="flex items-center justify-between pl-3 pr-1 text-[10.5px] font-bold uppercase tracking-[1px] text-tertiary">{title}{action}</div>
      {children}
    </div>
  );
}
const Count = ({ n }: { n: number }) => (n > 0 ? <span className="num text-[12px] text-tertiary">{n}</span> : null);

/* The one-time nudge on Today for a phone browser: the app is the better
   home for a phone, said once, dismissed for good. Persisted in this
   browser only — it is about this browser. */
const NUDGE_KEY = 'habishine.appNudge.dismissed';
export function PhoneNudge() {
  const [hidden, setHidden] = useState(() => { try { return localStorage.getItem(NUDGE_KEY) === '1'; } catch { return false; } });
  if (hidden) return null;
  const dismiss = () => { setHidden(true); try { localStorage.setItem(NUDGE_KEY, '1'); } catch { /* private mode */ } };
  return (
    <div className="rise-in mt-4 flex items-center gap-3 rounded-row p-3" style={{ background: 'linear-gradient(135deg, var(--sky-dawn-top), var(--sky-dawn-horizon))' }}>
      <SunMark size={36} />
      <a href={APP_STORE_URL} target="_blank" rel="noopener" className="min-w-0 flex-1">
        <span className="block text-[14px] font-bold text-ink">Habishine is better as an app</span>
        <span className="block text-[12.5px] leading-snug text-ink-2">Reminders and the Lock Screen card need it. Get it on the App Store.</span>
      </a>
      <button type="button" aria-label="Dismiss" onClick={dismiss} className="rounded-full p-1.5 text-meta active:bg-ink/10"><Icon name="x" size={15} /></button>
    </div>
  );
}
